import { Catharsis, CatharsisArgs, CompNodes, getCatharsis, IStructureMegaStore } from '@wix/thunderbolt-catharsis'
import { multi, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	appConstant,
	CatharsisStructureStoreSymbol,
	CompNodeProviderSymbol,
	DataNodeProviderSymbol,
	MaterializerFactorySymbol,
	OnInvalidationSymbol,
} from './symbols'
import type { CompNodesProvider, DataNodes } from './types'
import type { RefSchemas } from '@wix/thunderbolt-becky-types'

function initializeCatharsis(
	onInvalidations: Array<Function>,
	compNodesProviders: Array<CompNodesProvider>,
	dataNodesProviders: Array<DataNodes>,
	materializerFactory: any /* Not importing MaterializerFactory due to a possible issue with the build caused by @wix/thunderbolt-types */,
	structureStore: IStructureMegaStore<any> | undefined,
	schemas: RefSchemas
): Catharsis {
	const componentNodes = compNodesProviders.reduce((acc, feature) => {
		Object.assign(acc, feature.getCompNodes())
		return acc
	}, {} as CompNodes<any>)

	const dataNodes = dataNodesProviders.reduce((acc, nodes) => Object.assign(acc, nodes), {})
	const catharsisArgs: CatharsisArgs = {
		extensions: {
			componentNodes,
			dataNodes,
			schemas,
		},
		structureStore,
		onInvalidation(path: Array<string>, get: <T = any>(path: string | Array<string | number>) => T): void {
			onInvalidations.forEach((onInvalidation) => onInvalidation(path, get))
		},
		materializerFactory,
	}

	return getCatharsis(catharsisArgs)
}

export const CatharsisFactory = withDependencies(
	[
		multi(OnInvalidationSymbol),
		multi(CompNodeProviderSymbol),
		multi(DataNodeProviderSymbol),
		MaterializerFactorySymbol,
		optional(CatharsisStructureStoreSymbol),
		appConstant('schemas'),
	] as const,
	initializeCatharsis
)

export const CatharsisFactoryFunction = withDependencies(
	[
		multi(CompNodeProviderSymbol),
		multi(DataNodeProviderSymbol),
		MaterializerFactorySymbol,
		appConstant('schemas'),
	] as const,
	(
		compNodesProviders,
		dataNodesProviders,
		materializerFactory,
		schemas
	): ((onInvalidations: Array<Function>, structureStore: IStructureMegaStore<any>) => Catharsis) => {
		return (onInvalidations: Array<Function>, structureStore: IStructureMegaStore<any>) =>
			initializeCatharsis(
				onInvalidations,
				compNodesProviders,
				dataNodesProviders,
				materializerFactory,
				structureStore,
				schemas
			)
	}
)
